import { StatusCodes } from 'http-status-codes';
import { getAuthState } from './auth';
import { operationStatusEndpoint } from './apiEndpoints';
import {
  MAX_RETRIES_REACHED_ERROR,
  OPERATION_STATUSES,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TEXT_NETWORK_ERROR,
  TOAST_TYPE_ERROR,
} from './constants';
import { logConsoleIfAllowed } from './debugging';
import { getFileUuidFromPath } from './getFileUuidFromPath';

import { handleUnauthorizedResponseAndReload, getBaseUrl } from '@/Utils/commonUtils';
import ShowToast from '@/Components/Toast/showToastTemplate';
import { checkInternet } from '@/Utils/checkInternetConnectionUtil';
import ShowToastWithDelay from '@/Components/Toast/showToastWithDelay';
import { isRanByCypress } from './isRanByCypress';
import { getUserData } from './userSession';

const url = getBaseUrl('api');

const checkForTooManyRequests = (endpoint, response) => {
  try {
    if (response?.status === StatusCodes.TOO_MANY_REQUESTS) {
      const username = getUserData().email;

      ShowToastWithDelay({
        type: TOAST_TYPE_ERROR,
        text: `Too many simultaneous requests from your username ${username}. Please wait a few minutes and try again.`,
        endpoint: endpoint,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        responseStatus: response?.status,
        autoClose: false,
        closeOnClick: true,
        toastId: 'toast_429',
      });
    }
  } catch (e) {
    console.error(e);
  }
};

export const headers = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (isRanByCypress()) {
    headers['X-GIGASHEET-TOKEN'] = localStorage.getItem('cypressGigasheetAPIToken');
    headers['WithC'] = 'true';
  } else {
    // Need token to be undefined for backend
    let { token } = getAuthState();
    if (token === null) {
      token = undefined;
    }
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Fetch with retry logic for network-related errors
const fetchWithRetry = async (
  url,
  options = {},
  callerFileAndFunctionName,
  retries = 3,
  delay = 1000
) => {
  const fileAndFunction = callerFileAndFunctionName
    ? callerFileAndFunctionName
    : '__FILE_AND_FUNCTION_NAME__';
  for (let i = 0; i < retries; i++) {
    try {
      return await fetch(url, options);
    } catch (error) {
      // Check if the error is network-related (TypeError or AbortError)
      if (error instanceof TypeError || error.name === 'AbortError') {
        console.warn(`Network issue or fetch aborted, retrying... (${i + 1}/${retries})`);

        // Retry only for network-related errors
        if (i < retries - 1) {
          await new Promise((resolve) => setTimeout(resolve, delay)); // Wait before retrying
        } else {
          // On the last retry, determine the correct error message
          const isInternetUp = await checkInternet();
          const errorMessage = isInternetUp
            ? TOAST_TEXT_GENERAL_SPREADSHEET_ERROR
            : TOAST_TEXT_NETWORK_ERROR;

          ShowToastWithDelay({
            type: TOAST_TYPE_ERROR,
            text: errorMessage,
            endpoint: url,
            errorContext: error,
            fileAndFunction: fileAndFunction,
            autoClose: false,
            closeOnClick: true,
            closeButton: true,
            noGraphics: true,
            toastId: 'network-error-toast',
          });
          throw new Error(`${MAX_RETRIES_REACHED_ERROR}: ${error.message}`);
        }
      } else {
        // For non-network errors, throw the error
        throw error;
      }
    }
  }
};

export async function get(
  route,
  returnRaw = false,
  errorContextOnFailure = null,
  callerFileAndFunctionName = null
) {
  let response = null;
  try {
    const requestOptions = {
      method: 'GET',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
    };
    if (isRanByCypress()) {
      requestOptions.headers['X-GIGASHEET-TOKEN'] = localStorage.getItem(
        'cypressGigasheetAPIToken'
      );
      requestOptions.headers['WithC'] = true;
    } else {
      // Need token to be undefined for backend
      let { token } = getAuthState();
      if (token === null) {
        token = undefined;
      }
      requestOptions.headers['Authorization'] = `Bearer ${token}`;
    }
    response = await fetchWithRetry(`${url}/${route}`, requestOptions, callerFileAndFunctionName);
    checkForTooManyRequests(route, response);

    if (response.status === StatusCodes.UNAUTHORIZED)
      handleUnauthorizedResponseAndReload(response, 'api-get');
    if (returnRaw) return response;

    return response.json();
  } catch (err) {
    if (
      errorContextOnFailure &&
      !err?.message?.toLowerCase().includes(MAX_RETRIES_REACHED_ERROR.toLowerCase())
    ) {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: errorContextOnFailure?.text || TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
        errorContext: err,
        endpoint: route,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        responseStatus: response?.status,
      });
    }
    if (returnRaw) return response;
    throw new Error(`GET error: ${err}`);
  }
}

export async function post(route, data, options = {}) {
  let timeoutId;

  try {
    const response = await postRaw(route, data, options);
    if (response.status === StatusCodes.UNAUTHORIZED)
      handleUnauthorizedResponseAndReload(response, 'api-post');
    const uuid = getFileUuidFromPath();
    if (response.status === 202) {
      const pollInterval = 5000;

      return new Promise((resolve, reject) => {
        const pollStatusCode = async () => {
          try {
            const operationStatusResponse = await get(operationStatusEndpoint(uuid));
            if (operationStatusResponse.Status === OPERATION_STATUSES.DONE) {
              logConsoleIfAllowed('Success! Status code Done received.');
              const json = await response.json();
              resolve({ ...json, Success: true, status: response.status });
              clearTimeout(timeoutId);
            }
            if (operationStatusResponse.Status === OPERATION_STATUSES.ERROR) {
              logConsoleIfAllowed('Error response received:', operationStatusResponse.Status);

              resolve({ operationStatusResponse, Success: false, status: response.status });
              clearTimeout(timeoutId);
            }
            if (operationStatusResponse.Status === OPERATION_STATUSES.WORKING) {
              logConsoleIfAllowed('Continue polling...');
              timeoutId = setTimeout(pollStatusCode, pollInterval);
            }
            if (!operationStatusResponse.Status) {
              reject({ operationStatusResponse, Success: false, status: response.status });
            }
          } catch (error) {
            logConsoleIfAllowed('Error during polling:', error.message);
            reject(new Error(`Polling error: ${error.message}`));
            clearTimeout(timeoutId);
          }
        };
        uuid !== 'datasets' && pollStatusCode();
      });
    } else {
      clearTimeout(timeoutId);
      const json = await response.json();
      return { ...json, status: response.status };
    }
  } catch (err) {
    clearTimeout(timeoutId);
    logConsoleIfAllowed('POST error:', err.message);
    throw new Error(`POST error: ${err.message}`);
  }
}

export function postRaw(route, data, options = {}) {
  const requestOptions = {
    method: 'POST',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    ...options,
  };
  if (isRanByCypress()) {
    requestOptions.headers['X-GIGASHEET-TOKEN'] = localStorage.getItem('cypressGigasheetAPIToken');
    requestOptions.headers['WithC'] = true;
  } else {
    // Need token to be undefined for backend
    let { token } = getAuthState();
    if (token === null) {
      token = undefined;
    }
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }
  const responsePromise = fetch(`${url}/${route}`, requestOptions);
  responsePromise.then((response) => {
    checkForTooManyRequests(route, response);
  });

  return responsePromise;
}

export async function http_delete(route, identifier) {
  try {
    const requestOptions = {
      method: 'DELETE',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
    };
    if (isRanByCypress()) {
      requestOptions.headers['X-GIGASHEET-TOKEN'] = localStorage.getItem(
        'cypressGigasheetAPIToken'
      );
      requestOptions.headers['WithC'] = true;
    } else {
      // Need token to be undefined for backend
      let { token } = getAuthState();
      if (token === null) {
        token = undefined;
      }
      requestOptions.headers['Authorization'] = `Bearer ${token}`;
    }

    const urlPath = identifier ? `${url}/${route}/${identifier}` : `${url}/${route}`;

    const response = await fetch(urlPath, requestOptions);
    checkForTooManyRequests(route, response);

    return await response.json();
  } catch (err) {
    throw new Error(`POST error: ${err}`);
  }
}

export async function httpDeleteWithBody(route, identifier, body) {
  let timeoutId;

  try {
    const requestOptions = {
      method: 'DELETE',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };
    if (isRanByCypress()) {
      requestOptions.headers['X-GIGASHEET-TOKEN'] = localStorage.getItem(
        'cypressGigasheetAPIToken'
      );
      requestOptions.headers['WithC'] = true;
    } else {
      // Need token to be undefined for backend
      let { token } = getAuthState();
      if (token === null) {
        token = undefined;
      }
      requestOptions.headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${url}/${route}/${identifier}`, requestOptions);
    checkForTooManyRequests(route, response);
    const uuid = getFileUuidFromPath();

    if (response.status === 202) {
      const pollInterval = 5000;

      return new Promise((resolve, reject) => {
        const pollStatusCode = async () => {
          try {
            const operationStatusResponse = await get(operationStatusEndpoint(uuid));
            if (operationStatusResponse.Status === OPERATION_STATUSES.DONE) {
              logConsoleIfAllowed('Success! Status code Done received.');
              const json = await response.json();
              resolve({ ...json, Success: true });
              clearTimeout(timeoutId);
            }
            if (operationStatusResponse.Status === OPERATION_STATUSES.ERROR) {
              logConsoleIfAllowed('Error response received:', operationStatusResponse.Status);

              resolve({ operationStatusResponse, Success: false });
              clearTimeout(timeoutId);
            }
            if (operationStatusResponse.Status === OPERATION_STATUSES.WORKING) {
              logConsoleIfAllowed('Continue polling...');
              timeoutId = setTimeout(pollStatusCode, pollInterval);
            }
            if (!operationStatusResponse.Status) {
              reject({ operationStatusResponse, Success: false });
            }
          } catch (error) {
            logConsoleIfAllowed('Error during polling:', error.message);
            reject(new Error(`Polling error: ${error.message}`));
            clearTimeout(timeoutId);
          }
        };
        uuid !== 'datasets' && pollStatusCode();
      });
    } else {
      logConsoleIfAllowed('Directly returning JSON response...');
      clearTimeout(timeoutId);
      return await response.json();
    }
  } catch (err) {
    clearTimeout(timeoutId);
    logConsoleIfAllowed('POST error:', err.message);
    throw new Error(`POST error: ${err}`);
  }
}

export async function put(route, data, opts) {
  try {
    const response = await putRaw(route, data, opts);
    checkForTooManyRequests(route, response);
    return await response.json();
  } catch (err) {
    throw new Error(`PUT error: ${err}`);
  }
}

export async function patch(route, data, opts) {
  try {
    const response = await patchRaw(route, data, opts);
    checkForTooManyRequests(route, response);
    return await response.json();
  } catch (err) {
    throw new Error(`PATCH error: ${err}`);
  }
}

export async function putRaw(route, data, opts = {}) {
  return putOrPatchRaw('PUT', route, data, opts);
}

export async function patchRaw(route, data, opts = {}) {
  return putOrPatchRaw('PATCH', route, data, opts);
}

export async function putOrPatchRaw(method, route, data, opts = {}) {
  const requestOptions = {
    method: method,
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    ...opts,
  };
  if (isRanByCypress()) {
    requestOptions.headers['X-GIGASHEET-TOKEN'] = localStorage.getItem('cypressGigasheetAPIToken');
    requestOptions.headers['WithC'] = true;
  } else {
    // Need token to be undefined for backend
    let { token } = getAuthState();
    if (token === null) {
      token = undefined;
    }
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }
  const responsePromise = fetch(`${url}/${route}`, requestOptions);
  responsePromise.then((response) => {
    checkForTooManyRequests(route, response);
  });

  return responsePromise;
}
